import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { GridOptions } from 'ag-grid-community';
import { IFileColumn, IFileHeader, IUploadContext, IUploadResponse, UploadDataService } from './upload-data.service';
import { UPLOAD_WIZARD, UploadService } from './upload.service';
import { FormattingService } from '../../shared/services/formatting.service';
import { TranslationService } from '../../shared/translation/translation.service';
import { UploadResultsComponent } from './upload-results.component';
import { StateService } from '@uirouter/angular';
import { STATE_UPLOAD_WIZARD_STEP4 } from '../../shared/app-states';
import _ from 'underscore';
import { UntypedFormGroup } from '@angular/forms';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../../shared/formly/ga-formly.service';
import { IObject } from '../../../type_definitions/datatypes';
import { UploadValidationDialogComponent } from './upload-validation-dialog.component';
import { IColDef } from '../../shared/report/report-grid.service';
import { GaWizard } from '../../shared/wizard/ga-wizard';
import { GaWizardService } from '../../shared/wizard/ga-wizard.service';
import { CellRendererComp } from '../../shared/ag-grid-components/ga-ag-grid.component';
import { DialogSize, GaMessageService, IDialog } from '../../shared/message/ga-message.service';
import { GaAgGridService } from '../../shared/ag-grid-components/ga-ag-grid.service';
import { GRID_UPLOAD_STEP_THREE } from '@app/shared/app-grids';

const SAVE_ACTION:string = 'save';

@Component({
  selector: 'uploads-step-three',
  template: `
    <h2>Sheets</h2>
    <p [innerHTML]="('GA.UPLOADS.SHEETS_INFO' | translate)"></p>
    <formly [form]="form" [fields]="fields" [model]="model"></formly>

    <h2>Columns</h2>
    <p [innerHTML]="('GA.UPLOADS.COLUMNS_INFO' | translate)"></p>

    <div class="col-space-top">
      <ga-ag-grid [gridId]="gridId" [gridOptions]="gridOptions" [gridInit]="gridInit" [height]="400"></ga-ag-grid>

      <div class="col-space-top">
        <button class="btn btn-primary" translate (click)="upload(true)" [busyButton]="testBusy">GA.BUTTONS.TEST_UPLOAD</button>
      </div>
    </div>
  `
})
export class UploadStepThreeComponent implements OnInit, OnDestroy {
  @Input() public code:string;

  public gridId:string = GRID_UPLOAD_STEP_THREE;
  public gridOptions:GridOptions;
  public gridInit:boolean;
  public testBusy:boolean;
  public form:UntypedFormGroup = new UntypedFormGroup({});
  public fields:IExtraFieldConfigurationObject[];
  public model:IObject = {};

  private wizard:GaWizard;

  constructor(
    private wizardService:GaWizardService,
    private formattingService:FormattingService,
    private translationService:TranslationService,
    private uploadService:UploadService,
    private uploadDataService:UploadDataService,
    private stateService:StateService,
    private formlyService:GaFormlyService,
    private messageService:GaMessageService,
    private gaAgGridService:GaAgGridService
  ) {
    /**/
  }

  public ngOnInit():void {
    this.wizard = this.wizardService.getInstance(UPLOAD_WIZARD);
    this.wizard.title = 'GA.UPLOADS.SELECT_COLUMNS';

    // init ag-grid
    if (this.uploadService.model?.uploadContext) {
      this.installGrid(this.uploadService.model.uploadContext);
    }

    this.model.sheetName = { id: this.uploadService.selectedSheetName };

    // create formly fields
    this.fields = this.createFields();

    // set wizard actions
    this.wizard.actions([
      this.wizardService.createActionButton(
        SAVE_ACTION,
        this.translationService.instant('GA.BUTTONS.UPLOAD'),
        'btn btn-success',
        this.upload
      )
    ]);
  }

  public ngOnDestroy():void {
    this.wizard.actions([]);
  }

  private installGrid(data:IUploadContext):void {
    this.gridOptions = {
      columnDefs: this.getColDefs(data),
      rowData: this.getRowData(data),
      suppressContextMenu: true
    };

    this.gridInit = true;
  }

  private getColDefs(data:IUploadContext):IColDef[] {
    const colDefs:IColDef[] = _.map(data.columns, (column:IFileColumn):IColDef => {
      return {
        field: column.colId.toString(),
        headerComponent: CellRendererComp.SELECT_HEADER,
        headerComponentParams: {
          modelValue: column,
          items: data.headers,
          formatting: this.formattingService.formatName,
          trackBy: 'code',
          onValueChange: this.onHeaderValueChange
        }
      };
    });

    return colDefs;
  }

  private getRowData(d:IUploadContext):any {
    const preparedData:any[] = [];

    _.each(d.data?.data, (data:any[], dataIndex:number) => {
      _.each(d.columns, (column:IFileColumn, colIndex:number) => {
        if (!preparedData[ dataIndex ]) {
          preparedData[ dataIndex ] = {};
        }

        preparedData[ dataIndex ][ column.colId.toString() ] = data[ colIndex ];
      });
    });

    return preparedData;
  }

  private onHeaderValueChange = (newValue:IFileHeader, _oldValue:any, params:IObject):void => {
    const uploadContext:IUploadContext = this.uploadService.model.uploadContext;
    const columnIndex:number = _.findIndex(uploadContext.columns, { colId: params.colId });
    uploadContext.columns[ columnIndex ] = _.extend(uploadContext.columns[ columnIndex ], newValue);

    this.uploadService.model = _.extend(this.uploadService.model, { uploadContext: uploadContext });
  };

  public upload = (test:boolean = false):void => {
    const uploadContext:IUploadContext = this.uploadService.model.uploadContext;

    this.wizard.setActionBusy(SAVE_ACTION, true);
    this.wizard.setActionEnabled(SAVE_ACTION, false);
    this.testBusy = true;

    this.uploadDataService.submitColumns(test, this.uploadService.selectedSheetName, uploadContext.columns, uploadContext.importId, this.uploadService.model.context)
      .then((res:IUploadResponse) => {
        // when status = true, it means all values are correct
        // when status = false, it means some columns value are incorrect
        if (res.status) {
          if (test) {
            // show results dialog because it's in test mode
            this.showResultsDialog(res);
          } else {
            // go to results tab
            this.stateService.go(STATE_UPLOAD_WIZARD_STEP4);
          }
        } else {
          // show validation dialog to let user correct the values
          this.showValidationDialog(res);
        }
      })
      .finally(() => {
        this.wizard.setActionBusy(SAVE_ACTION, false);
        this.wizard.setActionEnabled(SAVE_ACTION, true);
        this.testBusy = false;
      });
  };

  private showValidationDialog(result:IUploadResponse):void {
    const dialog:IDialog = this.messageService.showDialog({
      component: UploadValidationDialogComponent,
      wrapperComponent: false,
      size: DialogSize.XL,
      backdrop: 'static',
      inputs: {
        results: result
      },
      outputs: {
        onDialogClose: () => dialog.hide()
      }
    });
  }

  private showResultsDialog(result:IUploadResponse):void {
    const dialog:IDialog = this.messageService.showDialog({
      component: UploadResultsComponent,
      wrapperComponent: false,
      backdrop: 'static',
      inputs: {
        inDialog: true,
        code: this.code,
        result
      },
      outputs: {
        onDialogClose: () => dialog.hide()
      }
    });
  }

  private createFields():IExtraFieldConfigurationObject[] {
    return [
      this.formlyService.aSelect('sheetName')
        .withoutWrapper()
        .onChange(this.onSheetChange)
        .withFormat('id')
        .withOptions(_.map(this.uploadService.model.uploadContext?.sheets, (sheet:string) => {
          return { id: sheet };
        }))
        .build()
    ];
  }

  private onSheetChange = (newValue:any):void => {
    this.uploadService.model = _.extend(this.uploadService.model, { sheetName: newValue?.id });

    if (this.uploadService.model?.sheetName) {
      this.gaAgGridService.setGridLoading(this.gridId, true);
      this.uploadDataService.getUploadContext(this.uploadService.model?.uploadContext?.importId, this.uploadService.model?.sheetName)
        .then((context:IUploadContext) => {
          // update shared context
          this.uploadService.model = _.extend(this.uploadService.model, { uploadContext: context });

          // update grid data
          this.gaAgGridService.setRowData(this.gridId, this.getRowData(context));
        })
        .finally(() => {
          this.gaAgGridService.setGridLoading(this.gridId, false);
        });
    }

  };
}